// OIDC discovery doc fetch + module-level cache. The doc carries the
// AS endpoints the SPA hits for /authorize, /token, /revoke,
// /end_session, and the JWKS URI for ID-token verification.
//
// Cached for the lifetime of the SPA module: discovery payloads rarely
// change, and the JWKS URL inside the doc is itself separately cached
// in the verifier. A page reload re-fetches.

import type { components } from "@content-buddy/genapi/openapi_oidcas";
import { getRuntimeConfig } from "../runtimeConfig";

type DiscoveryDoc = components["schemas"]["oidcas.v1.OpenIdConfigurationResponse"];

// Strip any trailing slash so `${oidcBase()}/path` never doubles up
// ("http://auth.7f000001.nip.io/" -> "http://auth.7f000001.nip.io"). Falls back to same-origin.
// Read at call time, not module load: the runtime config is only populated
// once loadRuntimeConfig() resolves, which happens before the app renders.
function oidcBase(): string {
  return (getRuntimeConfig().oidcUrl || "/oidc").replace(/\/+$/, "");
}

let inflight: Promise<DiscoveryDoc> | null = null;
let cached: DiscoveryDoc | null = null;

export async function getDiscovery(): Promise<DiscoveryDoc> {
  if (cached) return cached;
  if (!inflight) {
    inflight = fetch(`${oidcBase()}/.well-known/openid-configuration`, { credentials: "include" })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`discovery fetch failed: ${resp.status}`);
        const doc = (await resp.json()) as DiscoveryDoc;
        cached = doc;
        return doc;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function resetDiscoveryCache(): void {
  cached = null;
  inflight = null;
}
